import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IUserFilter } from "../models/UserFilter";
import { userService } from "../services/userService";
import { userQueryKey } from "./QueryKey";
import { IPaginatedResponse } from "../models/Pagination";
import {
  IChangePassword,
  ICreateUser,
  IUserPermission,
  IUserSystem,
} from "../models/Users";

export function useUsersWithFilters(
  filters: IUserFilter,
): UseQueryResult<IPaginatedResponse<IUserSystem>> {
  return useQuery({
    queryKey: [userQueryKey.USERS_WITH_FILTERS, filters],
    queryFn: () => userService.getUsersWithFilters(filters),
    enabled: !!filters,
  });
}
export function useCreateUser(): UseMutationResult<
  IUserSystem,
  Error,
  ICreateUser
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateUser) => userService.createUser(data),
    onSuccess: (newUser) => {
      queryClient.setQueryData(
        [userQueryKey.USERS_WITH_FILTERS],
        (oldData: IPaginatedResponse<IUserSystem> | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: [newUser, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
}
export function useUpdateUser(): UseMutationResult<
  IUserSystem,
  Error,
  { id: string; data: Partial<IUserSystem> }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => userService.updateUser(id, data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(
        [userQueryKey.USERS_WITH_FILTERS],
        (oldData: IPaginatedResponse<IUserSystem> | undefined) => {
          if (!oldData) return oldData;
          const updatedItems = oldData.data.map((user) =>
            user.id === updatedUser.id ? updatedUser : user,
          );
          return {
            ...oldData,
            data: updatedItems,
            total: oldData.pagination.total,
          };
        },
      );
    },
  });
}
export function useAuthorizeUser(): UseMutationResult<
  IUserPermission,
  Error,
  { id: string; permission: number[] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permission }) =>
      userService.authorizeUser(id, permission),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(
        [userQueryKey.PERMISSIONS_BY_USER, updatedUser.id],
        (oldData: IUserPermission | undefined) => {
          if (!oldData) return oldData;
          return { ...oldData, permissions: updatedUser.permissions };
        },
      );
    },
  });
}
export function useGetPermissionsByUser(
  id: string,
): UseQueryResult<IUserPermission> {
  return useQuery({
    queryKey: [userQueryKey.PERMISSIONS_BY_USER, id],
    queryFn: () => userService.getPermissionsByUser(id),
  });
}

export function useChangePassword(): UseMutationResult<
  boolean,
  Error,
  { id: string; password: string }
> {
  return useMutation({
    mutationFn: ({ id, password }) => userService.changePassword(id, password),
  });
}
export function useChangePasswordByUser(): UseMutationResult<
  boolean,
  Error,
  IChangePassword
> {
  return useMutation({
    mutationFn: (data) => userService.changePasswordByUser(data),
  });
}

export function useUploadAvatar(): UseMutationResult<string, Error, File> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => userService.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [userQueryKey.USERS_WITH_FILTERS],
      });
    },
  });
}
